import { TableCellTypes } from '@carboledger/ui';
import { makeUnitTypeCell, makeUnitScopedCell } from '@/components/common';
import { TRANSACTION_LEDGER_TYPE_TONE } from '@/config/statusTones';
import { buildTransactionTypeVariants } from '@/config/statuses';
import { TRANSACTION_COLUMNS } from './transactions.constants';
import { buildRowUnitOptions, unitTypeLabel } from './transactionUnits';

// One entry per column, in design order. Nothing is sortable — the route offers no orderBy.

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderQuantity = makeUnitScopedCell('quantity');
const renderBalance = makeUnitScopedCell('balance');

// Every column pins, per Figma — the header carries the pin control on all of them.
const textColumn = (id, header, width = 180) => ({
  id,
  header,
  type: TableCellTypes.text,
  width,
  pinnable: true,
});

export const buildTransactionsColumns = (t, { unitHeaderSelect }) => [
  textColumn(TRANSACTION_COLUMNS.serialNo, t('TRANSACTIONS_COL_SERIAL_NO'), 69),
  textColumn(TRANSACTION_COLUMNS.declarationNo, t('TRANSACTIONS_COL_DECLARATION_NO'), 140),
  textColumn(TRANSACTION_COLUMNS.associatedId, t('TRANSACTIONS_COL_ASSOCIATED_ID'), 140),
  textColumn(TRANSACTION_COLUMNS.materialName, t('FIELD_MATERIAL_NAME'), 200),
  textColumn(TRANSACTION_COLUMNS.associatedSite, t('TRANSACTIONS_COL_ASSOCIATED_SITE'), 143),
  // TODO: the design puts an info icon here, but no tooltip copy exists for it yet.
  textColumn(TRANSACTION_COLUMNS.terminal, t('FIELD_TERMINAL'), 140),
  {
    ...textColumn(TRANSACTION_COLUMNS.type, t('TRANSACTIONS_COL_TYPE'), 180),
    type: TableCellTypes.badge,
    variantMap: buildTransactionTypeVariants(t, TRANSACTION_LEDGER_TYPE_TONE),
  },
  {
    ...textColumn(
      TRANSACTION_COLUMNS.unitType,
      t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
      252,
    ),
    type: TableCellTypes.custom,
    headerSelect: unitHeaderSelect,
    cell: renderUnitTypeCell,
  },
  {
    ...textColumn(TRANSACTION_COLUMNS.quantity, t('FIELD_QUANTITY'), 140),
    type: TableCellTypes.custom,
    cell: renderQuantity,
  },
  {
    ...textColumn(TRANSACTION_COLUMNS.balance, t('TRANSACTIONS_COL_BALANCE'), 160),
    type: TableCellTypes.custom,
    cell: renderBalance,
  },
  // TODO: the design also has a "Shipping to/from" column, which `Transaction` has no field for.
  textColumn(TRANSACTION_COLUMNS.sendTo, t('ALLOCATIONS_COL_SEND_TO'), 160),
  {
    ...textColumn(TRANSACTION_COLUMNS.transactionDate, t('FIELD_TRANSACTION_DATE'), 160),
    type: TableCellTypes.date,
  },
  textColumn(TRANSACTION_COLUMNS.ghgEmission, t('FIELD_GHG_EMISSIONS'), 180),
  textColumn(TRANSACTION_COLUMNS.ghgEmissionSavings, t('FIELD_GHG_EMISSION_SAVINGS'), 180),
  textColumn(TRANSACTION_COLUMNS.countryOfOrigin, t('FIELD_COUNTRY_OF_ORIGIN'), 180),
  textColumn(TRANSACTION_COLUMNS.rawMaterialType, t('FIELD_RAW_MATERIAL_TYPE'), 180),
  // TODO: the design puts an info icon here, but no tooltip copy exists for it yet.
  textColumn(TRANSACTION_COLUMNS.feedstockCategory, t('FIELD_FEEDSTOCK_CATEGORY'), 180),
  // TODO: the design puts an info icon here, but no tooltip copy exists for it yet.
  textColumn(TRANSACTION_COLUMNS.allocationPreference, t('FIELD_ALLOCATION_PREFERENCE'), 180),
  {
    ...textColumn(
      TRANSACTION_COLUMNS.massBalancePeriod,
      t('ALLOCATIONS_COL_MASS_BALANCE_PERIOD'),
      260,
    ),
    description: t('TRANSACTIONS_TOOLTIP_MASS_BALANCE_PERIOD'),
  },
  textColumn(TRANSACTION_COLUMNS.transactedBy, t('TRANSACTIONS_COL_TRANSACTED_BY'), 279),
];
